/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react"
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Box,
  Breadcrumbs,
  InputAdornment,
} from "@mui/material"
import {
  StyledBreadCrumbContainer,
  StyledLink
} from "../employee_visibility/styles"
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query"
import api from "../../../api"
import authenticatedFetch from "../../../api/authenticatedFetch"
import { Loader } from "../../../components/loader"
import { IndexTable } from "../../../components/index_table"
import camelize from "../../../utils/camelize"
import { useNotificationContext } from "../../../contexts/notificationContext"
import moment from "moment";
import { formatDate } from "../../../utils/formatDate"
import dayjs from "dayjs"
import {
  StyledBox,
  StyledTypography,
  StyledSearchBar,
  StyledDivider,
} from "../global_employee_search/styles"
import SearchIcon from '@mui/icons-material/Search';
import { useDebounce } from "../../../utils/useDebounce"
import { Query_Keys } from "../../../lib/queryKeys"

export const UninvitedGuestPage = () => {
  const [itemsPerPage, setItemsPerPage] = React.useState(25)
  const [page, setPage] = React.useState(1)
  const [sortBy, setSortBy] = React.useState('')
  const [sortOrder, setSortOrder] = React.useState('desc')
  const notification = useNotificationContext()
  const queryClient = useQueryClient()
  const [csvLoading, setCsvLoading] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearchTerm = useDebounce(search, 300);

  const fetchUninvitedGuests = async () => {
    const response = await api.get('/v1/admin/network_guests/uninvited_guests',
      {
        params:
        {
          per_page: itemsPerPage,
          page: page,
          search: debouncedSearchTerm,
          ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {})
        }
      }
    )
    return { uninvitedGuests: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: Query_Keys.uninvited_guests({
      queryFilter: debouncedSearchTerm,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchUninvitedGuests,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=unknown_guests`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `unknown_guest_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      notification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      notification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const tableColumns = [
    { key: 'id', sortable: true },
    { key: 'first_name', sortable: true },
    { key: 'last_name', sortable: true },
    { key: 'email', sortable: true },
    { key: 'date_of_birth', sortable: true },
    { key: 'phone_number', sortable: true },
    { key: 'created_at', sortable: true }
  ]

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        first_name: record?.first_name,
        last_name: record?.last_name,
        email: record?.email,
        date_of_birth: record?.date_of_birth ? moment.utc(record?.date_of_birth).format("MM/DD/YYYY") : '-',
        phone_number: record?.phone_number,
        created_at: formatDate(record?.created_at, record?.time_zone_abbr)
      };
    });
  };

  const rows = TableRows(data?.uninvitedGuests);

  const handleSearch = (searchQuery) => {
    queryClient.removeQueries({ queryKey: ["uninvited_guests"]})
    setSearch(searchQuery)
    setTimeout(() => {
      setPage(1)
    }, 1000)
  }

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: "scroll",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Reports</StyledLink>
            <StyledLink color={"#515152"}>Unknown Network Guests</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <StyledBox report={false}>
          <StyledTypography>Unknown Network Guests Report</StyledTypography>
          <StyledSearchBar
            value={search}
            placeholder="Search"
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
            }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
          />
        </StyledBox>
        <StyledDivider />
        <div style={{marginBottom: '16px'}} ></div>
        {
          isLoading && !search ? (
            <Loader />
          ) : (
            <IndexTable
              columns={columns}
              data={rows}
              tableId="uninvited-guests"
              type="entries"
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={data?.pageCount}
              checkboxes={false}
              report={false}
              hideNavigation={false}
              showDownloadButton={true}
              disableDownloadButton={data?.uninvitedGuests?.length === 0 || csvLoading}
              handleExportCsv={handleExportCsv}
              csvLoading={csvLoading}
              searchLoading={isFetching && !!debouncedSearchTerm}
              hasSearchTerm={!!debouncedSearchTerm}
            />
          )
        }
      </Box>
    </React.Fragment>
  )
}
